import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Mail, RefreshCw, AlertTriangle } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useSeo } from "../lib/useSeo";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const fmtMoney = (pence, currency) => {
  if (!pence) return "—";
  const cur = (currency || "GBP").toUpperCase();
  const sym = cur === "GBP" ? "£" : cur === "USD" ? "$" : cur === "EUR" ? "€" : "";
  return `${sym}${(pence / 100).toFixed(2)}`;
};

export default function AdminAbandoned() {
  useSeo({ title: "Abandoned checkouts", noindex: true });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/abandoned");
      setRows(r.data.rows || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const downloadCsv = async () => {
    try {
      const r = await api.get("/admin/abandoned.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `abandoned-checkouts-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = filter
    ? rows.filter((r) =>
        (r.email + " " + r.name + " " + r.plan_label).toLowerCase().includes(filter.toLowerCase()))
    : rows;

  const mailAll = () => {
    const emails = filtered.map((r) => r.email).filter(Boolean).join(",");
    if (!emails) { toast.error("No emails to send to"); return; }
    const subject = encodeURIComponent("Your AI Shorthand pass: which shortcut do you need today?");
    const body = encodeURIComponent(
      "Hi,\n\nI noticed you started checkout on AI Shorthand but haven't picked up your pass yet.\n\n" +
      "If you've got an overdue invoice to chase, a difficult customer email to send, or a marketing post to write, try these first:\n" +
      "  /invoice-chase\n  /complaint-letter\n  /marketing-campaign\n\n" +
      "Log in and paste your rough notes into the 'Help Me Fill This In' box — the AI takes it from there.\n\n" +
      "https://web.aipromptshortcuts.app/login\n\nBest,\nSarah — XpressPrompts");
    // BCC keeps addresses hidden from each recipient
    window.location.href = `mailto:?bcc=${emails}&subject=${subject}&body=${body}`;
  };

  const totalAmount = filtered.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div data-testid="admin-abandoned-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-xl font-bold text-[#0B0F0D]">Abandoned checkouts</h2>
          <p className="text-xs text-slate-500 mt-1">
            Accounts that clicked a plan but never paid. Latest attempt per person.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={load} data-testid="abandoned-refresh"
            className="inline-flex items-center gap-1.5 text-sm font-semibold border border-slate-200 hover:bg-slate-50 rounded-xl px-3 py-2 transition-colors">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={downloadCsv} data-testid="abandoned-csv-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 rounded-xl px-3 py-2 transition-colors">
            <Download size={14} /> Download CSV
          </button>
          <button onClick={mailAll} disabled={filtered.length === 0} data-testid="abandoned-mail-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl px-3 py-2 disabled:opacity-50 transition-colors">
            <Mail size={14} /> Email all (BCC)
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Abandoned</div>
          <div className="text-2xl font-display font-bold text-[#0B0F0D]" data-testid="abandoned-count">{rows.length}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Lost revenue</div>
          <div className="text-2xl font-display font-bold text-amber-700">{fmtMoney(totalAmount, "GBP")}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">On trial flow</div>
          <div className="text-2xl font-display font-bold text-[#0B0F0D]">{rows.filter((r) => r.trial).length}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Used voucher</div>
          <div className="text-2xl font-display font-bold text-[#0B0F0D]">{rows.filter((r) => r.voucher).length}</div>
        </div>
      </div>

      <input
        placeholder="Filter by email, name or plan…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        data-testid="abandoned-filter"
        className="w-full sm:max-w-md mb-4 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-emerald-500"
      />

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <AlertTriangle size={20} className="inline-block mr-2 text-amber-500" />
            {rows.length === 0 ? "No abandoned checkouts. Nice." : "No matches for that filter."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                <tr>
                  <th className="text-left p-3 font-bold">Email</th>
                  <th className="text-left p-3 font-bold">Name</th>
                  <th className="text-left p-3 font-bold">Plan</th>
                  <th className="text-right p-3 font-bold">Amount</th>
                  <th className="text-left p-3 font-bold">Voucher</th>
                  <th className="text-left p-3 font-bold">Type</th>
                  <th className="text-left p-3 font-bold">Started</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.user_id} data-testid={`abandoned-row-${r.user_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-3">
                      <a href={`mailto:${r.email}`} className="text-emerald-700 font-medium hover:underline">{r.email}</a>
                    </td>
                    <td className="p-3 text-slate-700">{r.name || "—"}</td>
                    <td className="p-3">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
                        {r.plan_label || r.plan || "—"}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono text-slate-800">{fmtMoney(r.amount, r.currency)}</td>
                    <td className="p-3 text-slate-500 text-xs font-mono">{r.voucher || "—"}</td>
                    <td className="p-3">
                      {r.trial ? (
                        <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Trial</span>
                      ) : (
                        <span className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">Paid</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-500 text-xs whitespace-nowrap">{fmtDate(r.started_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400 mt-4 max-w-2xl">
        "Email all (BCC)" opens your default mail client with the visible list pre-filled as BCC recipients and a suggested subject and body. Edit the message before sending — your mail app is the sender, not the site.
      </p>
    </div>
  );
}
